import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Toaster } from 'react-hot-toast'
import { Sheet, SheetRef } from 'react-modal-sheet'
import { useSearchStore, ITEMS_PER_PAGE } from '../../store/jpeTopPageStore'
import { SortFilterBar } from './nureseries/SortFilterBar'
import { NurseryItem } from './nureseries/NurseryItem'
import { NurseryItemSkeleton } from './nureseries/NurseryItemSkeleton'
import { EmptyState } from './nureseries/EmptyState'
import { DesktopFilterBadges } from './search-elements/DesktopFilterBadges'
import { Pagination } from './nureseries/Pagination'

export const DraggableBottomSheet: React.FC = () => {
  const {
    nurseries,
    selectedNurseryId,
    isSearchModalOpen,
    isFilterModalOpen,
    isDesktop,
    shouldStartCollapsed,
    setShouldStartCollapsed,
    currentPage,
    setCurrentPage,
    loading,
    fetchCheckInMonths,
    checkInMonthsData,
  } = useSearchStore()

  const [_currentSnapIndex, setCurrentSnapIndex] = useState(1) // Start at middle position
  const sheetRef = useRef<SheetRef>(null)
  const desktopScrollRef = useRef<HTMLDivElement>(null)
  const mobileScrollRef = useRef<HTMLDivElement>(null)

  // Calculate pagination
  const totalPages = Math.ceil(nurseries.length / ITEMS_PER_PAGE)
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE
  const endIndex = startIndex + ITEMS_PER_PAGE
  const paginatedNurseries = nurseries.slice(startIndex, endIndex)
  // Get device-specific spacing values
  const getDeviceSpacing = () => {
    const width = window.innerWidth
    const height = window.innerHeight
    let spacing: {
      headerPadding: string
      contentPadding: string
      itemMargin: string
    }

    // Small devices (iPhone SE, etc.)
    if (width <= 375 || height <= 667) {
      spacing = {
        headerPadding: 'py-2',
        contentPadding: 'px-3 pb-3 pt-2',
        itemMargin: 'mb-3',
      }
    }
    // Large devices (iPhone 13 Pro Max, etc.)
    else if (width >= 414 || height >= 844) {
      spacing = {
        headerPadding: 'py-4',
        contentPadding: 'px-5 pb-5 pt-4',
        itemMargin: 'mb-8',
      }
    }
    // Standard devices (most phones)
    else {
      spacing = {
        headerPadding: 'py-3',
        contentPadding: 'px-4 pb-4 pt-3',
        itemMargin: 'mb-4',
      }
    }
    return spacing
  }

  // Helper function to get safe-area-inset-top value
  const getSafeAreaInsetTop = () => {
    // Try to get the safe-area-inset-top value from CSS
    // This is mainly for iOS devices with notches
    const testDiv = document.createElement('div')
    testDiv.style.paddingTop = 'env(safe-area-inset-top)'
    document.body.appendChild(testDiv)
    const computedValue = window.getComputedStyle(testDiv).paddingTop
    if (testDiv.parentNode) {
      testDiv.parentNode.removeChild(testDiv)
    }

    // Parse the pixel value
    const insetValue = parseInt(computedValue) || 0
    return insetValue
  }

  // Calculate dynamic snap points based on viewport height
  const getSnapPoints = () => {
    const viewportHeight = window.innerHeight
    const screenWidth = window.innerWidth
    const isSmallScreen = screenWidth < 640 // Tailwind 'sm' breakpoint

    const topPadding = isSmallScreen ? 12 : 16
    const searchBarHeight = 40
    const bottomSpacing = isSmallScreen ? 12 : 16
    const desiredVisualGap = topPadding + searchBarHeight + bottomSpacing

    const libraryOffset = 34
    const safeAreaInsetTop = getSafeAreaInsetTop()

    const maxSnapHeight = viewportHeight - desiredVisualGap + libraryOffset + safeAreaInsetTop
    let maxSnapPercentage = maxSnapHeight / viewportHeight

    if (maxSnapPercentage > 1.0) {
      maxSnapPercentage = 1.0
    }

    // Define minimum heights in pixels for each snap point
    const minSnapHeight = 100 // Minimum collapsed height
    const midSnapHeight = Math.max(viewportHeight * 0.6, 400) // At least 400px for mid

    // Convert to percentages - Only 3 snap points allowed
    const snapPoints = [
      Math.max(minSnapHeight / viewportHeight, 0.12),
      Math.min(midSnapHeight / viewportHeight, 0.6),
      maxSnapPercentage,
    ]

    return snapPoints
  }

  const [snapPoints, setSnapPoints] = useState(getSnapPoints())
  const [deviceSpacing, setDeviceSpacing] = useState(getDeviceSpacing())

  // Update snap points and spacing on window resize
  useEffect(() => {
    const handleResize = () => {
      const newSnapPoints = getSnapPoints()
      const newDeviceSpacing = getDeviceSpacing()
      setSnapPoints(newSnapPoints)
      setDeviceSpacing(newDeviceSpacing)
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const initialSnapPosition = shouldStartCollapsed ? 1 : 2

  useEffect(() => {
    if (shouldStartCollapsed) {
      const timer = setTimeout(() => {
        setShouldStartCollapsed(false)
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [shouldStartCollapsed, setShouldStartCollapsed])

  // Reset page if current page exceeds total pages (e.g., after reload or filter change)
  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages)
    }
  }, [totalPages, currentPage, setCurrentPage])

  // Scroll to top when page changes
  useEffect(() => {
    if (isDesktop && desktopScrollRef.current) {
      desktopScrollRef.current.scrollTo({ top: 0, behavior: 'smooth' })
    } else if (!isDesktop && mobileScrollRef.current) {
      mobileScrollRef.current.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [currentPage, isDesktop])

  useEffect(() => {
    if (loading || paginatedNurseries.length === 0) return

    const idsToFetch = paginatedNurseries.map((nursery) => nursery.kintoneId).filter((id) => !checkInMonthsData[id])

    if (idsToFetch.length > 0) {
      fetchCheckInMonths(idsToFetch)
    }
  }, [paginatedNurseries, loading, checkInMonthsData, fetchCheckInMonths])

  // Handle snap position changes
  const handleSnap = (index: number) => {
    const validSnapIndices = [1, 2, 3]
    if (!validSnapIndices.includes(index)) {
      let nearestIndex = 2
      if (index <= 0) {
        nearestIndex = 1
      } else if (index >= 4) {
        nearestIndex = 3
      }

      setTimeout(() => {
        sheetRef.current?.snapTo(nearestIndex)
      }, 0)
      return
    }

    setCurrentSnapIndex(index)
  }

  if (!isDesktop && (isSearchModalOpen || isFilterModalOpen)) {
    return null
  }

  if (!nurseries) {
    return null
  }

  if (isDesktop) {
    return (
      <div className="h-full" style={{ maxHeight: '100vh' }}>
        {createPortal(
          <Toaster
            position="top-center"
            containerStyle={{
              zIndex: 99999,
            }}
            toastOptions={{
              style: {
                zIndex: 99999,
              },
            }}
          />,
          document.body,
        )}
        <div className="h-full flex flex-col overflow-hidden">
          <div className="shrink-0">
            <DesktopFilterBadges />
          </div>

          <div ref={desktopScrollRef} className="overflow-auto h-full">
            {loading ? (
              <div className="p-4">
                {[...Array(3)].map((_, index) => (
                  <div key={`skeleton-${index}`} className="mb-4">
                    <NurseryItemSkeleton />
                  </div>
                ))}
              </div>
            ) : nurseries.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <EmptyState />
              </div>
            ) : (
              <div className="p-4">
                {paginatedNurseries.map((nursery, index) => (
                  <NurseryItem key={`${nursery?.id}-${index}`} nursery={nursery} isSelected={nursery.id === selectedNurseryId} />
                ))}
                <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      {createPortal(
        <Toaster
          position="top-center"
          containerStyle={{
            top: 20,
            zIndex: 99999,
          }}
          toastOptions={{
            style: {
              zIndex: 99999,
            },
          }}
        />,
        document.body,
      )}
      <Sheet
        ref={sheetRef}
        isOpen={true}
        onClose={() => {}}
        snapPoints={snapPoints}
        initialSnap={initialSnapPosition}
        disableDrag={false}
        disableDismiss={true}
        onSnap={handleSnap}
        tweenConfig={{ ease: 'easeOut', duration: 0.3 }}
      >
        <Sheet.Backdrop style={{ backgroundColor: 'transparent' }} />
        <Sheet.Container
          style={{
            boxShadow: 'none',
          }}
        >
          <Sheet.Header>
            <SortFilterBar />
          </Sheet.Header>

          <Sheet.Content
            style={{
              height: '100%',
            }}
            disableDrag
          >
            <div
              ref={mobileScrollRef}
              style={{
                overflowY: 'auto',
                WebkitOverflowScrolling: 'touch',
                height: '100%',
              }}
            >
              {loading ? (
                <div
                  className={deviceSpacing.contentPadding}
                  style={{
                    marginBottom: '20px',
                  }}
                >
                  {[...Array(3)].map((_, index) => (
                    <div key={`skeleton-${index}`} className={deviceSpacing.itemMargin}>
                      <NurseryItemSkeleton />
                    </div>
                  ))}
                </div>
              ) : nurseries.length === 0 ? (
                <EmptyState />
              ) : (
                <div
                  className={deviceSpacing.contentPadding}
                  style={{
                    marginBottom: '20px',
                  }}
                >
                  {paginatedNurseries.map((nursery, index) => (
                    <div key={`${nursery?.id}-${index}`} className={deviceSpacing.itemMargin}>
                      <NurseryItem nursery={nursery} isSelected={nursery.id === selectedNurseryId} />
                    </div>
                  ))}
                  <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
                </div>
              )}
            </div>
          </Sheet.Content>
        </Sheet.Container>
      </Sheet>
    </>
  )
}
